import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { pagedQuery, pagedResponse, PickerIdsSchema } from './pagination.js';
import { AuthorshipSchema, CurrencySchema, IsoDateSchema, MessageSchema, MoneyStringSchema, PctStringSchema, } from './primitives.js';
const c = initContract();
/**
 * Employees & job roles (HR-01, PART 12).
 *
 * THE FIELD-LEVEL SALARY RULE (HR-01 §9, owner ruling R15): EmployeeSchema
 * carries NO pay field — not masked, ABSENT. The pay profile is its own
 * shape on its own endpoints, gated payroll.view / payroll.edit, which the
 * Owner alone holds until granted (BR-PERM-39). The e2e suite asserts the
 * general read never contains a pay key.
 *
 * Job roles (owner ruling R11, BR-HR-01-05a..d): a tenant-managed list. A
 * role points at SURFACES — the closed set of person-asking dropdowns — and
 * NEVER grants a permission (BR-HR-01-17). Managing the list is the job-role
 * configuration permission, which this build maps to `admin.edit` (tenant
 * configuration — deciding who appears in approval/cashier dropdowns across
 * the product), deliberately distinct from the employee-write `hr.edit` that
 * ASSIGNS an existing role (BR-HR-01-05b; the 12×5 grid may not grow private
 * actions, 16.5.2a).
 */
// ── Vocabularies ────────────────────────────────────────────────────────────
export const HR_DEPARTMENTS = ['finance', 'sales', 'operations', 'retail', 'hr', 'it'];
export const EMPLOYEE_STATUSES = ['active', 'on_leave', 'terminated', 'suspended'];
export const PAY_FREQUENCIES = ['monthly', 'biweekly', 'weekly'];
export const PAY_METHODS = ['bank_transfer', 'cash', 'check'];
export const JOB_ROLE_SURFACE_KEYS = [
    'sales_rep',
    'warehouse_manager',
    'approver',
    'cashier',
    'accountant',
    'driver',
];
/** The per-tenant seeds (BR-HR-01-05a) — defaults and nothing more. */
export const SEED_JOB_ROLES = [
    {
        name: 'Sales Rep',
        nameAr: 'مندوب مبيعات',
        surfaces: ['sales_rep'],
        consequence: 'Appears in the Sales Rep field on invoices and quotations',
        consequenceAr: 'يظهر في حقل مندوب المبيعات على الفواتير وعروض الأسعار',
    },
    {
        name: 'Warehouse Manager',
        nameAr: 'مدير مستودع',
        surfaces: ['warehouse_manager'],
        consequence: 'Selectable as a warehouse manager',
        consequenceAr: 'قابل للاختيار كمدير مستودع',
    },
    {
        name: 'Approver',
        nameAr: 'معتمِد',
        surfaces: ['approver'],
        consequence: 'Eligible for Journal Entry / Voucher approvals',
        consequenceAr: 'مؤهل لاعتماد القيود والسندات',
    },
    {
        name: 'Cashier',
        nameAr: 'أمين صندوق',
        surfaces: ['cashier'],
        consequence: 'Selectable at the point of sale and on receipt vouchers',
        consequenceAr: 'قابل للاختيار في نقطة البيع وسندات القبض',
    },
    {
        name: 'Accountant',
        nameAr: 'محاسب',
        surfaces: ['accountant'],
        consequence: 'Selectable for accounting entries',
        consequenceAr: 'قابل للاختيار للقيود المحاسبية',
    },
    {
        name: 'Driver',
        nameAr: 'سائق',
        surfaces: ['driver'],
        consequence: 'Selectable on delivery notes',
        consequenceAr: 'قابل للاختيار على سندات التسليم',
    },
    { name: 'Other', nameAr: 'أخرى', surfaces: [], consequence: null, consequenceAr: null },
];
// ── The fixed sentences of HR-01 / 16.6A ────────────────────────────────────
export const HR_MESSAGES = {
    /** ERR-HR-01-01. */
    incomplete: 'Fill in the name, job title, department, status and hire date.',
    /** ERR-HR-01-02. */
    terminationDateMissing: 'Give the last working day.',
    /** ERR-HR-01-03. */
    terminationBeforeHire: 'The last day must be on or after the hire date.',
    /** ERR-HR-01-04 / BR-HR-01-13. */
    hasHistory: (name) => `${name} has attendance and payroll history. Terminate their record instead.`,
    /** BR-HR-01-05b. */
    jobRoleConfigDenied: 'You do not have permission to change the job-role list. Ask an administrator to grant it.',
    /** BR-PERM-25's confirmation, on terminating somebody with an account. */
    leaverHandled: (name) => `${name} has been marked as having left. Their account has been suspended and their pending invitation cancelled.`,
    /** BR-PERM-77 — the Owner's account is never suspended by leaver handling. */
    ownerAccountStays: 'This is the owner’s account, so it stays active. Transfer ownership to close it.',
    /** BR-HR-01-19 — a terminated employee's invitation refuses on open. */
    invitationInvalid: 'This person has left the company.',
    /**
     * BR-HR-01-21 / MD-10-D4 — the leaver whose approved, billable hours have
     * not reached an invoice yet. The refusal is CLEARABLE by design: bill them
     * (PRJ-04's generate stamps the entries) or reject the week, and the
     * termination goes through unchanged.
     *
     * THE HOURS CARRY TWO DECIMALS AND THE SPEC PRINTS THREE. Spec line 28289
     * reads "12.500 hours", and `timesheet_entries.hours` is `numeric(6, 2)` —
     * a third decimal is unrepresentable, so printing one would assert a
     * precision the column cannot hold. The figure here is the database's own
     * `sum(hours)::text`, which is the same string every other hours total in
     * PART 13 is built from (`timesheets.service.ts`, `project-billing`), so
     * one person's hours read identically wherever they appear.
     */
    hoursWaitingToBeBilled: (name, hours) => `${name} has ${hours} hours waiting to be billed. Bill or reject them first.`,
    /**
     * MD-10-D5 / BR-PERM-25 / BR-PERM-30, under owner ruling D24 option C
     * (26 Aug 2026): "refuse unlink/re-point while the employee is active;
     * mark-as-left switches the login off, then re-link."
     *
     * THE HOLE THIS SENTENCE GUARDS. MD-10-D5 makes termination suspend "any
     * account joined to them" — so severing the join FIRST empties the set the
     * rule quantifies over, and the leaver keeps a live login. Re-pointing at a
     * different account is the same hole wearing a value instead of a null: the
     * previously joined account is stranded either way. One sentence covers
     * both, because they are one act — changing who this record is joined to.
     *
     * WHY IT IS STATIC AND NAMES NOBODY. Its sibling `hoursWaitingToBeBilled`
     * names a person, which makes it a function, which makes it unmappable by
     * `STATIC_MESSAGE_KEYS` in apps/web — parameterised sentences reach an
     * Arabic reader in English. This one is a fixed string precisely so that it
     * CAN be mapped. The key is drafted and deferred, not forgotten: see
     * `d24-lockdown--i18n-keys.json` at the repository root, held by ruling
     * because no screen sends `userId` yet, to be requested by the wave that
     * builds link management. THE EN TEXT THERE MUST MATCH THIS BYTE FOR BYTE.
     *
     * WHY THE REMEDY NAMES TAKING THEM BACK ON, WHICH READS ODD FOR SOMEBODY WHO
     * NEVER LEFT. It is the path the product actually has, and it was measured
     * rather than assumed: `EmployeesService.update` already refuses ANY edit to
     * a terminated record unless the same request carries a status
     * (`HR_MESSAGES.hasHistory`) — "a terminated record is read for history".
     * So the correction is ONE request, `{ status: 'active', userId: <right> }`,
     * which rehires and re-links together. Saying "then link the new one" would
     * have described a request the server refuses.
     */
    accountLinkLockedWhileEmployed: 'You cannot unlink or change the account joined to somebody who still works here. Mark them as having left first — that switches their account off — then link the right account when you take them back on.',
    /**
     * BR-HR-01-23 — "an employee's assigned warehouses limit where they may
     * move stock". Raised by the movement writer (the one choke point every
     * quantity change goes through), never by any screen's own guess. It NAMES
     * THE WAREHOUSE, so it is parameterised and rides D46's structured shape —
     * see `HR_REFUSALS.stockOutsideAssignedWarehouses` below, which is what the
     * writer actually throws; this twin composes the English sentence.
     */
    stockOutsideAssignedWarehouses: (warehouse) => `You cannot move stock in ${warehouse}. Your employee record limits you to your assigned warehouses — ask for ${warehouse} to be added on the Employee List.`,
};
/**
 * D46's structured refusals for HR — key + params beside the composed English
 * sentence, so an Arabic reader gets the refusal in Arabic even though it
 * names a warehouse. The census (`apps/api/test/refusal-census.spec.ts`)
 * holds this builder to its `HR_MESSAGES` twin member for member.
 */
export const HR_REFUSALS = {
    stockOutsideAssignedWarehouses: (warehouse) => ({
        message: HR_MESSAGES.stockOutsideAssignedWarehouses(warehouse),
        key: 'hr.errStockOutsideAssignedWarehouses',
        params: { warehouse },
    }),
    // ── d46tail: HR-01's two remaining parameterised refusals ─────────────────
    /** BR-HR-01-13 — a hired employee cannot be deleted; `name` is a display
     *  value. Raised by the delete and reactivate doors. */
    hasHistory: (name) => ({
        message: HR_MESSAGES.hasHistory(name),
        key: 'hr.errHasHistory',
        params: { name },
    }),
    /** BR-HR-01-21 — `hours` is the database's own two-decimal `sum(hours)::text`,
     *  a pre-formatted display string (the twin's comment above governs). */
    hoursWaitingToBeBilled: (name, hours) => ({
        message: HR_MESSAGES.hoursWaitingToBeBilled(name, hours),
        key: 'hr.errHoursWaitingToBeBilled',
        params: { name, hours },
    }),
};
// ── Shapes ──────────────────────────────────────────────────────────────────
export const JobRoleSchema = z
    .object({
    id: z.uuid(),
    name: z.string(),
    nameAr: z.string(),
    surfaces: z.enum(JOB_ROLE_SURFACE_KEYS).array(),
    consequence: z.string().nullable(),
    consequenceAr: z.string().nullable(),
    active: z.boolean(),
    /** How many employees currently carry it (Q-HR-01-3's warning input). */
    holders: z.number().int(),
})
    .strict();
/**
 * The general employee read — hr.view. NO PAY FIELDS EXIST ON THIS SHAPE
 * (R15: absent, not masked). `.strict()` makes an accidental leak a contract
 * violation, not a quiet extra key.
 *
 * ── WHY THE AUTHORSHIP FIELDS ARRIVE WITH THE STAMPING AND NOT BEFORE ──────
 *
 * `employees` is the ninth of the nine master tables 20260901100000 gave
 * `created_by` / `updated_by`. On the other eight the write paths and this
 * shape landed together; here the columns landed alone, because every
 * `employees` write lives in `apps/api/src/hr/`, which belonged to a
 * different wave that week. This shape was therefore left DELIBERATELY
 * without the fields — so the server would not record an author it never
 * shows — and the two halves are restored in one change.
 *
 * The half-state was the thing being avoided, in both directions: stamping
 * without exposing records a fact nobody can read, and exposing without
 * stamping shows six permanent NULLs that look like a product that forgot.
 *
 * The authorship fields are NOT pay fields and R15 does not reach them: who
 * edited a record is not what that person is paid. The pay profile keeps its
 * own shape on its own payroll.view-gated endpoints, and gains nothing here.
 *
 * `createdAt` was already on this shape as a lone field and is now supplied
 * by the spread — same name, same type, same value, following the eight
 * siblings rather than keeping a private copy beside them.
 */
/**
 * ERR-HR-01-06 / Q-HR-01-3, ruled by A4 (delegated rulings, 26 Aug 2026):
 * "warn when switching off a job role somebody relies on — warn when last
 * holder or named on an open document." One entry per role the update
 * SWITCHED OFF that trips either trigger; the update itself proceeds — the
 * spec's answer is a warning, not a refusal.
 *
 * The names travel as DATA (both scripts), never as a composed sentence, so
 * the client renders the warning in the reader's language and nothing here
 * touches the exact-match server-message map — a deliberately structured
 * shape, the same reasoning as ruling D46's key+params for refusals.
 */
export const RoleRemovalWarningSchema = z
    .object({
    jobRoleId: z.uuid(),
    name: z.string(),
    nameAr: z.string(),
    /** No other non-terminated employee still carries this role. */
    lastHolder: z.boolean(),
    /**
     * Open documents that currently name this person THROUGH one of the
     * switched-off role's surfaces: pending leave requests and payroll runs
     * awaiting approval that route to them (approver), and draft/sent
     * quotations naming them as sales rep (sales_rep). The other four
     * surfaces name no employee on any document today — measured, not
     * assumed; the enumeration lives beside the query in
     * apps/api/src/hr/employees.service.ts.
     */
    openDocuments: z.number().int(),
})
    .strict();
export const EmployeeSchema = z
    .object({
    id: z.uuid(),
    code: z.string(),
    employeeNo: z.number().int(),
    fullName: z.string(),
    fullNameAr: z.string(),
    jobTitle: z.string(),
    jobTitleAr: z.string(),
    department: z.enum(HR_DEPARTMENTS),
    status: z.enum(EMPLOYEE_STATUSES),
    hireDate: z.string(),
    terminationDate: z.string().nullable(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    address: z.string().nullable(),
    dateOfBirth: z.string().nullable(),
    /** BR-HR-01-12: stored (and served) partly masked. */
    nationalId: z.string().nullable(),
    emergencyName: z.string().nullable(),
    emergencyPhone: z.string().nullable(),
    emergencyRelationship: z.string().nullable(),
    managerId: z.uuid().nullable(),
    userId: z.uuid().nullable(),
    shiftId: z.uuid().nullable(),
    jobRoleIds: z.uuid().array(),
    /**
     * Wave 4D — the commission % a new sales document takes when this person
     * is its rep (the quotation's sales rep, or the employee linked to the
     * document's salesperson user). Null = none.
     */
    defaultCommissionPct: z.string().nullable(),
    /**
     * BR-HR-01-23: the warehouses this person may move stock within — and an
     * EMPTY ARRAY MEANS EVERY WAREHOUSE, never none, so `[]` is the ordinary
     * unrestricted state (the rule's own sentence; HR-01 §5b calls this the
     * only per-person scoping in the product). Rides the list read the way
     * `jobRoleIds` does. BR-HR-01-06's display condition (shown only while a
     * warehouse_manager-surface role is on) is the screen's, not the wire's:
     * the ids are always served, because dormant assignments survive a role
     * being switched off.
     */
    warehouseIds: z.uuid().array(),
    notes: z.string().nullable(),
    /**
     * ERR-HR-01-06 — present ONLY on an updateEmployee response whose role
     * change switched off a role somebody was relying on. Absent everywhere
     * else (list/get/create), so no read path pays for the check.
     */
    roleRemovalWarnings: RoleRemovalWarningSchema.array().optional(),
    // The six fields, `createdAt` among them — the shape the eight other
    // master contracts already carry. rule-coverage:ignore
    ...AuthorshipSchema.shape,
})
    .strict();
export const EmployeePayLineSchema = z
    .object({
    id: z.uuid(),
    kind: z.enum(['allowance', 'deduction']),
    name: z.string(),
    basis: z.enum(['fixed', 'percent']),
    value: z.string(),
    /** Percent deductions resolved against BASE ONLY (BR-HR-01-07). */
    amount: z.string(),
})
    .strict();
/** The payroll.view-gated profile (HR-01 §5d). */
export const EmployeePaySchema = z
    .object({
    employeeId: z.uuid(),
    baseSalary: z.string(),
    currency: z.string(),
    payFrequency: z.enum(PAY_FREQUENCIES),
    paymentMethod: z.enum(PAY_METHODS),
    salaryBankAccountId: z.uuid().nullable(),
    costRate: z.string(),
    billingRate: z.string(),
    lines: EmployeePayLineSchema.array(),
    totalAllowances: z.string(),
    totalDeductions: z.string(),
    /** salary + allowances − deductions (BR-HR-01-09), own currency. */
    netPay: z.string(),
})
    .strict();
/**
 * BR-HR-01-01: "An employee must have a code, a name, a job title, a
 * department, a status and a hire date." Four of the six are required HERE —
 * `fullName`, `jobTitle`, `department`, `hireDate` — and they are required by
 * being non-optional, so a create request omitting one is refused at the
 * boundary rather than reaching a service check.
 *
 * The other two are deliberately absent from this schema and are NOT gaps:
 *
 *  - THE CODE is server-assigned. `EmployeesService.create` calls
 *    `nextNumber(tx, 'employee')` unconditionally, so the code cannot be
 *    omitted because a client never supplies it. Accepting one here would let
 *    a caller choose it, which BR-HR-01-20 (a rehire reuses its original code,
 *    never reissued) needs the server to own.
 *  - THE STATUS is `NOT NULL DEFAULT 'active'` on the table. A new employee is
 *    active; the write schemas expose status only on UPDATE, and only as the
 *    three switchable values — terminating is its own action, because it drags
 *    BR-HR-01-18's account suspension along with it.
 *
 * So all six hold, across two layers, and `packages/db/src/schema/hr.ts`
 * carries the half this file does not.
 */
const EmployeeWriteBase = z.object({
    fullName: z.string().trim().min(1).max(160),
    fullNameAr: z.string().trim().min(1).max(160).optional(),
    jobTitle: z.string().trim().min(1).max(120),
    jobTitleAr: z.string().trim().min(1).max(120).optional(),
    department: z.enum(HR_DEPARTMENTS),
    hireDate: IsoDateSchema,
    phone: z.string().trim().max(40).optional(),
    email: z.string().trim().max(200).optional(),
    address: z.string().trim().max(300).optional(),
    dateOfBirth: IsoDateSchema.optional(),
    nationalId: z.string().trim().max(60).optional(),
    emergencyName: z.string().trim().max(120).optional(),
    emergencyPhone: z.string().trim().max(40).optional(),
    emergencyRelationship: z.string().trim().max(60).optional(),
    managerId: z.uuid().nullable().optional(),
    userId: z.uuid().nullable().optional(),
    shiftId: z.uuid().nullable().optional(),
    jobRoleIds: z.uuid().array().optional(),
    /** Wave 4D — the rep's default sales commission %; null clears it. */
    defaultCommissionPct: PctStringSchema.nullable().optional(),
    /**
     * BR-HR-01-23's assignment, written the way `jobRoleIds` is: the FULL
     * REPLACEMENT set, omitted = untouched. Sending `[]` is not a deletion of
     * anything meaningful — it is the unrestricted state the rule names
     * ("an empty list means every warehouse").
     */
    warehouseIds: z.uuid().array().optional(),
    notes: z.string().trim().max(2000).optional(),
});
export const CreateEmployeeRequestSchema = EmployeeWriteBase.strict();
export const UpdateEmployeeRequestSchema = EmployeeWriteBase.partial()
    .extend({
    /** active ⇄ on_leave ⇄ suspended here; terminating is its own action. */
    status: z.enum(['active', 'on_leave', 'suspended']).optional(),
})
    .strict();
export const PayLineInputSchema = z
    .object({
    kind: z.enum(['allowance', 'deduction']),
    name: z.string().trim().min(1).max(120),
    /** BR-HR-01-08: allowances are fixed — 'percent' refused for them. */
    basis: z.enum(['fixed', 'percent']).optional(),
    value: MoneyStringSchema,
})
    .strict();
export const UpdateEmployeePayRequestSchema = z
    .object({
    baseSalary: MoneyStringSchema.optional(),
    currency: CurrencySchema.optional(),
    payFrequency: z.enum(PAY_FREQUENCIES).optional(),
    paymentMethod: z.enum(PAY_METHODS).optional(),
    salaryBankAccountId: z.uuid().nullable().optional(),
    /**
     * BR-HR-01-22, first half: "cost rate and billing rate must not be below
     * zero." The pattern is anchored and its only numeric class is `\d`, so it
     * admits NO SIGN — a negative rate is UNREPRESENTABLE here rather than
     * rejected by a comparison. There is no `min(0)` to forget and no branch
     * to get backwards.
     *
     * `CHECK (cost_rate >= 0)` / `CHECK (billing_rate >= 0)` on the table say
     * the same thing again in SQL, so the floor survives any writer that never
     * passes through this schema.
     *
     * The rule's SECOND half — "changing either must not alter any timesheet
     * that has already been invoiced" — is not enforced by these lines and
     * needs no enforcement: timesheet pricing never reads these columns.
     * `resolveRates` (apps/api/src/projects/projects-shared.ts) prices an hour
     * from `project_rate_cards.hourly_rate` by the member's rate ROLE, and the
     * generated invoice line snapshots that rate. Safe by construction, not by
     * a margin — editing a rate here cannot reach an invoiced timesheet
     * because nothing joins the two.
     */
    costRate: z
        .string()
        .regex(/^\d{1,15}(\.\d{1,4})?$/)
        .optional(),
    billingRate: z
        .string()
        .regex(/^\d{1,15}(\.\d{1,4})?$/)
        .optional(),
    /** Full replacement of the allowance/deduction tables. */
    lines: PayLineInputSchema.array().optional(),
})
    .strict();
export const JobRoleWriteSchema = z
    .object({
    name: z.string().trim().min(1).max(80),
    nameAr: z.string().trim().min(1).max(80).optional(),
    surfaces: z.enum(JOB_ROLE_SURFACE_KEYS).array().optional(),
    consequence: z.string().trim().max(200).nullable().optional(),
    consequenceAr: z.string().trim().max(200).nullable().optional(),
    active: z.boolean().optional(),
})
    .strict();
/** A person-picker row: what a surface dropdown renders. Never a pay field. */
export const SurfacePersonSchema = z
    .object({
    employeeId: z.uuid(),
    code: z.string(),
    fullName: z.string(),
    fullNameAr: z.string(),
    jobTitle: z.string(),
})
    .strict();
export const JobRoleUpdateSchema = JobRoleWriteSchema.partial();
const employeeParams = z.object({ employeeId: z.uuid() }).strict();
export const hrContract = c.router({
    listEmployees: {
        method: 'GET',
        path: '/api/hr/employees',
        query: pagedQuery({
            department: z.enum(HR_DEPARTMENTS).optional(),
            includeTerminated: z.literal('true').optional(),
            /**
             * GEN-50's first read — HR-01 §6's search box: the name, or the
             * printed employee code, which are the two things the row shows.
             */
            q: z.string().trim().max(120).optional(),
            /**
             * GEN-50's second read — the people a form is ALREADY holding,
             * resolved by id and by nothing else. It ignores `includeTerminated`
             * deliberately: a leave request or an attendance correction written
             * for somebody who has since LEFT must still name them, and
             * BR-HR-01-13's exclusion is about who a picker OFFERS.
             */
            ids: PickerIdsSchema.optional(),
        }),
        responses: {
            200: pagedResponse('employees', EmployeeSchema),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'HR-01 list (hr.view), PAGED (NFR-42) — every filter in SQL. Pay fields are ABSENT from this shape (R15). `q`/`ids` are GEN-50’s two picker reads.',
    },
    getEmployee: {
        method: 'GET',
        path: '/api/hr/employees/:employeeId',
        pathParams: employeeParams,
        responses: { 200: EmployeeSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema },
        summary: 'One employee (hr.view) — identity, contact, roles; never pay.',
    },
    createEmployee: {
        method: 'POST',
        path: '/api/hr/employees',
        body: CreateEmployeeRequestSchema,
        responses: { 201: EmployeeSchema, 400: MessageSchema, 401: MessageSchema, 403: MessageSchema },
        summary: 'Add an employee (hr.create). Code EMP-#### allocated by the server.',
    },
    updateEmployee: {
        method: 'PATCH',
        path: '/api/hr/employees/:employeeId',
        pathParams: employeeParams,
        body: UpdateEmployeeRequestSchema,
        responses: {
            200: EmployeeSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Edit identity/contact/status, assign job roles, warehouses, manager, shift (hr.edit). Role changes ripple to dropdowns immediately (BR-HR-01-04); the warehouse set scopes stock moves (BR-HR-01-23).',
    },
    terminateEmployee: {
        method: 'POST',
        path: '/api/hr/employees/:employeeId/terminate',
        pathParams: employeeParams,
        body: z.object({ terminationDate: IsoDateSchema }).strict(),
        responses: {
            200: z.object({ employee: EmployeeSchema, message: z.string() }).strict(),
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'Record a leaver (hr.edit): sets Terminated + the date, and IN THE SAME ACTION suspends any joined account and cancels its invitation (BR-HR-01-18, BR-PERM-25) — except the Owner’s (BR-PERM-77). Both halves audited (BR-PERM-26).',
    },
    getEmployeePay: {
        method: 'GET',
        path: '/api/hr/employees/:employeeId/pay',
        pathParams: employeeParams,
        responses: { 200: EmployeePaySchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema },
        summary: 'The pay profile — payroll.view ONLY (R15). 403 for everybody else, hr.view included.',
    },
    updateEmployeePay: {
        method: 'PUT',
        path: '/api/hr/employees/:employeeId/pay',
        pathParams: employeeParams,
        body: UpdateEmployeePayRequestSchema,
        responses: {
            200: EmployeePaySchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'Edit the pay profile (payroll.edit): salary, allowances (fixed only — BR-HR-01-08), deductions (% against base only — BR-HR-01-07), rates. Salary bank account excludes credit cards (BR-HR-01-11).',
    },
    listJobRoles: {
        method: 'GET',
        path: '/api/hr/job-roles',
        query: z.object({ includeInactive: z.literal('true').optional() }).strict(),
        responses: {
            200: z.object({ jobRoles: JobRoleSchema.array() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'The tenant’s job-role list (hr.view) — active roles are the chips (BR-HR-01-05a).',
    },
    createJobRole: {
        method: 'POST',
        path: '/api/hr/job-roles',
        body: JobRoleWriteSchema,
        responses: { 201: JobRoleSchema, 400: MessageSchema, 401: MessageSchema, 403: MessageSchema, 409: MessageSchema },
        summary: 'Add a job role and point it at surfaces (the configuration permission — BR-HR-01-05b).',
    },
    updateJobRole: {
        method: 'PATCH',
        path: '/api/hr/job-roles/:jobRoleId',
        pathParams: z.object({ jobRoleId: z.uuid() }).strict(),
        body: JobRoleUpdateSchema,
        responses: {
            200: JobRoleSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Rename, deactivate, or re-point a job role. Deactivating stops the chips and the dropdown feed without erasing who held it (BR-HR-01-05c).',
    },
    listSurfacePeople: {
        method: 'GET',
        path: '/api/hr/people',
        query: z.object({ surface: z.enum(JOB_ROLE_SURFACE_KEYS) }).strict(),
        responses: {
            200: z.object({ people: SurfacePersonSchema.array() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'BR-HR-01-05d: who a surface dropdown offers — ACTIVE employees carrying an ACTIVE role that unlocks it, queried live (never copied). hr.view.',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=hr.js.map